import { AnalyticsBrowser } from '@segment/analytics-next'
import { type ClientOutputs, Integration } from '@botpress/client'
import { ReactNode, useEffect } from 'react'
import { User } from '../features/users/types'
import { ActiveWorkspace, useAppStore } from '../stores'
import { useAsync } from 'react-use'
import { config } from '../shared'
import { MenuItem } from '~/elements'
import { BotUseCase } from '@bpinternal/const'
import { Plan } from '~/features/billing/types'

const paywallAnalyticEvents = ['proactive_message', 'footer', 'allowed-origins'] as const
export type PaywallAnalyticEventName = (typeof paywallAnalyticEvents)[number]
type PaywallAnalyticEvent = {
  type: 'paywall_triggered'
  feature: PaywallAnalyticEventName
  fromPlan: Plan
  workspaceId?: string
  botId?: string
}

export type AnalyticEvent =
  | PaywallAnalyticEvent
  | {
      type: 'context_menu_opened'
      content: MenuItem[]
    }
  | {
      type: 'integration_installed' | 'integration_viewed'
      integration: Partial<Integration>
    }
  | {
      type: 'navigate_to_bot'
      from?: 'recent_bots' | 'all_bots'
    }
  | { type: 'update_configuration_variables' }
  | { type: 'create_bot'; botId: string }
  | { type: 'create_adk_bot'; botId: string }
  | { type: 'bot_type_dialog_presented' }
  | { type: 'bot_type_selected'; botType: 'studio' | 'adk' }
  | { type: 'bot_type_dialog_dismissed' }
  | { type: 'adk_onboarding_started'; botId: string; workspaceId: string }
  | { type: 'adk_onboarding_step_completed'; botId: string; workspaceId: string; step: number; stepName: string }
  | { type: 'adk_onboarding_completed'; botId: string; workspaceId: string }
  | { type: 'adk_onboarding_cli_setup_skipped'; botId: string; workspaceId: string }
  | { type: 'adk_onboarding_generate_pat_clicked'; botId: string; workspaceId: string }
  | { type: 'adk_onboarding_dev_server_opened'; botId: string; workspaceId: string }
  | { type: 'adk_onboarding_docs_clicked'; botId: string; workspaceId: string }
  | { type: 'adk_onboarding_github_clicked'; botId: string; workspaceId: string }
  | { type: 'open_bot_after_creation'; botId: string }
  | {
      type: 'update_workspace_profile'
      workspaceId: string
      public: boolean
      socialAccounts: number
      about: boolean
      email: boolean
      website: boolean
      profilePicture: boolean
    }
  | { type: 'update_handle'; workspaceId: string }
  | {
      type: 'update_addons'
      state: 'clicked' | 'confirmed'
      upgrade: boolean
      workspaceId: string
      plan: ClientOutputs['getWorkspace']['plan']
    }
  | {
      type: 'update_ai_spend'
      state: 'clicked' | 'confirmed' | 'credit_card_required' | 'clicked_add_card'
      workspaceId: string
      prevAiSpend: number
      newAiSpend: number
      plan: ClientOutputs['getWorkspace']['plan']
    }
  | {
      type: 'cancel_subscription'
      state: 'success' | 'errored'
      success: boolean
      workspaceId: string
      plan: ClientOutputs['getWorkspace']['plan']
    }
  | { type: 'analytics_date_picker_shortcut'; shortcut: string; workspaceId: string; botId: string }
  | { type: 'analytics_date_picker_submit'; workspaceId: string; botId: string }
  | {
      type: 'select_conversation'
      conversationId: string
      workspaceId: string
      botId: string
      integration: string
      channel: string
      tags: number
    }
  | {
      type: 'error_event'
      routeId?: string
      botId?: string
      workspaceId?: string
      conversationId?: string
      integrationId?: string
      userId?: string
      userEmail?: string
    }
  | {
      type: 'paywall_triggered'
      feature: string
      fromPlan: Plan
      workspaceId: string
    }
  | {
      type: 'request_copy_to_bot_from_dashboard'
      sourceBotId: string
      targetBotId: string
    }
  | {
      type: 'copy_to_bot_success_from_dashboard'
      sourceBotId: string
      targetBotId: string
    }
  | {
      type: 'copy_to_bot_error_from_dashboard'
      sourceBotId: string
      targetBotId: string
      error: string
    }
  | {
      type: 'hitl_conversation_assigned'
      agentId?: string
      patientId?: string
      conversationId: string
    }
  | {
      type: 'hitl_conversation_closed'
      agentId?: string
      agentEmail?: string
      patientId?: string
      conversationId: string
    }
  | {
      type: 'hitl_conversation_message_sent'
      agentId?: string
      patientId?: string
      conversationId: string
      messageType: string
    }
  | { type: 'onboarding_started'; botId: string; workspaceId: string }
  | { type: 'onboarding_name_bot'; botId: string; workspaceId: string }
  | { type: 'onboarding_skipped'; botId: string; workspaceId: string }
  | {
      type: 'onboarding_step_completed'
      botId: string
      workspaceId: string
      step: number
      stepName: string
    }
  | {
      type: 'onboarding_agent_type_selected'
      botId: string
      workspaceId: string
      useCase: BotUseCase
    }
  | {
      type: 'onboarding_context_extraction'
      botId: string
      workspaceId: string
      source: 'website' | 'text' | 'none'
    }
  | {
      type: 'onboarding_knowledge_base_usage'
      botId: string
      workspaceId: string
      knowledgeType: 'files' | 'urls' | 'richtext'
      count: number
    }
  | {
      type: 'onboarding_splash_screen_shown'
      botId: string
      workspaceId: string
    }
  | {
      type: 'onboarding_first_user_message'
      botId: string
      workspaceId: string
    }
  | {
      type: 'onboarding_upsell_clicked'
      botId: string
      workspaceId: string
    }
  | {
      type: 'onboarding_deploy_completed'
      botId: string
      workspaceId: string
      durationMs: number
    }
  | {
      type: 'webchat_config_saved'
      workspaceId: string
      botId: string
      mode: 'embedded' | 'custom_toggle' | 'fab_default'
    }
  | {
      type: 'chat_sound_toggle'
      enabled: boolean
      botId: string
      workspaceId: string
    }
  | {
      type: 'conversation_history_toggle'
      enabled: boolean
      botId: string
      workspaceId: string
    }
  | { type: 'get_expert_support'; workspaceId: string }
  | { type: 'onboarding_survey_presented' }
  | { type: 'onboarding_survey_completed' }
  | { type: 'post_signup_fork_presented' }
  | { type: 'post_signup_fork_selected'; choice: 'I want to build' | 'build for me' }

type PageEvent = {
  name: string
  botId?: string
  workspaceId?: string
  conversationId?: string
  integrationId?: string
}

type Props = {
  children: ReactNode
}
export const SegmentProvider = ({ children }: Props) => {
  const currentUser = useAppStore((state) => state.currentUser)
  const currentWorkspace = useAppStore((state) => state.activeWorkspace)

  useEffect(() => {
    const { segmentAnalyticsWriteKey } = config
    if (segmentAnalyticsWriteKey) {
      analytics.load({ writeKey: segmentAnalyticsWriteKey })
    }
  }, [])

  useAsync(async () => {
    if (currentUser) {
      await identifyUser({ user: currentUser, workspace: currentWorkspace })
    }
  }, [currentUser?.id, currentWorkspace?.id])

  return <>{children}</>
}

const analytics = new AnalyticsBrowser()

export function trackEvent(event: AnalyticEvent, callback?: Parameters<AnalyticsBrowser['track']>[2]) {
  const { type, ...data } = event
  void analytics.track(type, data, callback).catch((error: any) => {
    console.warn('unable to track event', error)
  })
}

export function trackPage(event: PageEvent, callback?: Parameters<AnalyticsBrowser['page']>[2]) {
  const { name, ...props } = event
  void analytics.page(name, props, callback).catch((error: any) => {
    console.warn('unable to track page', error)
  })
}

async function identifyUser({ user, workspace }: { user: User; workspace?: ActiveWorkspace }): Promise<void> {
  try {
    await analytics.identify(user.id, {
      email: user.email,
      createdAt: user.createdAt,
      emailVerified: user.emailVerified,
      displayName: user.displayName,
    })

    if (workspace) {
      await analytics.group(workspace.id, {
        createdAt: workspace.createdAt,
        updatedAt: workspace.updatedAt,
        handle: workspace.handle,
        name: workspace.name,
        email: workspace.contactEmail,
        plan: workspace.plan,
        website: workspace.website,
      })
    }
  } catch (error) {
    console.warn('Analytics error - identify', error)
  }
}
